import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { optimizeImageFile } from "../../services/images/optimizeImage";
import useAccessControl from "../context/useAccessControl";
import {
  COVER_DISPLAY_MODE_OPTIONS,
  buildHorarioResumen,
  createDefaultHorarioDetallado,
  emitRestaurantProfileUpdate,
  formatHorarioDia,
  getRestaurantProfile,
  mergeRemoteBranchIntoProfilePreservingLocalPreferences,
  normalizeCoverDisplayMode,
  normalizeKitchenModeForServiceMode,
  normalizeHorarioDetallado,
  normalizeKioskMenuMode,
  normalizeKioskStationCount,
  normalizeOperationalFeatureEnabled,
  normalizeServiceMode,
  saveRestaurantProfile,
} from "../../services/local/restaurantProfile";
import {
  BRAND_FONT_OPTIONS,
  BRAND_LOADING_ANIMATION_OPTIONS,
  buildRestaurantBrandTheme,
  extractBrandPaletteFromImage,
} from "../../services/local/brandTheme";
import { getPromotions } from "../../services/local/promotions";
import {
  DEFAULT_BRANCH_PAYMENT_PROVIDER,
  DEFAULT_BRANCH_PAYMENT_PROVIDER_STATUS,
  DEFAULT_BRANCH_PAYMENT_SPLIT_MODE,
  DEFAULT_KIOSK_PAYMENT_METHODS,
  DEFAULT_QR_PAYMENT_METHODS,
  normalizeCommissionRate,
  normalizePaymentMethods,
  normalizePaymentProvider,
  normalizePaymentProviderStatus,
  normalizePaymentSplitMode,
} from "../../services/local/paymentSettings";
import {
  createWaiterInvitation,
  getBranchPublicProfile,
  listBranchWaiters,
  listCompanyInvitations,
  updateWaiterBranchAccess,
  updateBranchPublicProfile,
} from "../../services/supabase/companyAdminService";
import { saveBranchPromotions } from "../../services/supabase/promotionsService";
import { getRuntimeEnvironment } from "../../services/local/runtimeEnvironment";
import BranchOperationalModulesPanel from "../common/BranchOperationalModulesPanel";
import { normalizeQrMenuStyle } from "../../services/local/qrMenuStyle";
import { normalizeQrCategoryOrder, normalizeQrSubcategoryOrder } from "../../services/local/qrCategoryOrder";
import QrMenuStyleEditor from "../common/QrMenuStyleEditor";

const chipsDisponibles = [
  "Familiar",
  "Autor",
  "Take away",
  "Salon",
  "Desayunos",
  "Cafe",
  "Veggie friendly",
  "Nocturno",
];

const createShift = (apertura = "20:00", cierre = "23:30") => ({
  apertura,
  cierre,
});


const normalizeProfileText = (value) =>
  value === undefined || value === null ? "" : `${value}`.trim();

const normalizeLoadingAnimationValue = (value) =>
  value === "pulse" || value === "float" ? value : "spin";

const buildBranchPublicProfileSyncPayload = (profile = {}, brandThemeOverride = null, includeCategoryOrder = false, includeSubcategoryOrder = false) => {
  const branding = profile?.branding || {};
  const primaryColor = brandThemeOverride?.primaryColor ?? profile?.primaryColor;
  const secondaryColor = brandThemeOverride?.secondaryColor ?? profile?.secondaryColor;
  const fontFamily = brandThemeOverride?.fontFamily ?? profile?.fontFamily;

  return {
    name: normalizeProfileText(profile?.nombre ?? profile?.name),
    phone: normalizeProfileText(profile?.telefono ?? profile?.phone),
    address: normalizeProfileText(profile?.direccion ?? profile?.address),
    description: normalizeProfileText(profile?.descripcion ?? profile?.description),
    logoUrl: normalizeProfileText(
      profile?.logoUrl || branding?.logoCompactUrl || branding?.logoPrimaryUrl || ""
    ),
    logoCompactUrl: normalizeProfileText(
      profile?.logoCompactUrl || branding?.logoCompactUrl || ""
    ),
    iconLogoUrl: normalizeProfileText(profile?.iconLogoUrl || branding?.iconLogoUrl || ""),
    loadingLogoUrl: normalizeProfileText(
      profile?.loadingLogoUrl || branding?.loadingLogoUrl || ""
    ),
    // La imagen del borrador es la fuente de verdad al guardar. Puede coexistir
    // una coverUrl antigua de una sincronizacion previa.
    coverUrl: normalizeProfileText(profile?.imagenPortada || profile?.coverUrl || ""),
    primaryColor: normalizeProfileText(primaryColor) || "#2563eb",
    secondaryColor: normalizeProfileText(secondaryColor) || "#f8fafc",
    fontFamily: normalizeProfileText(fontFamily),
    loadingAnimation: normalizeLoadingAnimationValue(
      profile?.loadingAnimation || branding?.loadingAnimation
    ),
    welcomeMessage: normalizeProfileText(
      profile?.welcomeMessage ?? profile?.mensajeBienvenida
    ),
    horarioDetallado: normalizeHorarioDetallado(profile?.horarioDetallado),
    coverDisplayMode: normalizeCoverDisplayMode(profile?.coverDisplayMode),
    qrMenuStyle: normalizeQrMenuStyle(profile?.qrMenuStyle),
    ...(includeCategoryOrder ? { qrCategoryOrder: normalizeQrCategoryOrder(profile?.qrCategoryOrder) } : {}),
    ...(includeSubcategoryOrder ? { qrSubcategoryOrder: normalizeQrSubcategoryOrder(profile?.qrSubcategoryOrder) } : {}),
    kitchenMode: normalizeKitchenModeForServiceMode(
      profile?.serviceMode,
      profile?.kitchenMode
    ),
    serviceMode: normalizeServiceMode(profile?.serviceMode),
    stockEnabled: normalizeOperationalFeatureEnabled(profile?.stockEnabled),
    waiterEnabled: normalizeOperationalFeatureEnabled(profile?.waiterEnabled),
    aiEnabled: normalizeOperationalFeatureEnabled(profile?.aiEnabled),
    kioskMenuMode: normalizeKioskMenuMode(profile?.kioskMenuMode),
    kioskStationCount: normalizeKioskStationCount(profile?.kioskStationCount),
    kioskDineInImageUrl: normalizeProfileText(profile?.kioskDineInImageUrl),
    kioskTakeawayImageUrl: normalizeProfileText(profile?.kioskTakeawayImageUrl),
    paymentProvider: normalizePaymentProvider(
      profile?.paymentProvider || DEFAULT_BRANCH_PAYMENT_PROVIDER
    ),
    paymentProviderStatus: normalizePaymentProviderStatus(
      profile?.paymentProviderStatus || DEFAULT_BRANCH_PAYMENT_PROVIDER_STATUS
    ),
    paymentAccountCbu: normalizeProfileText(profile?.paymentAccountCbu),
    paymentAccountAlias: normalizeProfileText(profile?.paymentAccountAlias),
    paymentLocationCity: normalizeProfileText(profile?.paymentLocationCity),
    paymentLocationState: normalizeProfileText(profile?.paymentLocationState),
    paymentLocationLatitude: normalizeProfileText(profile?.paymentLocationLatitude),
    paymentLocationLongitude: normalizeProfileText(profile?.paymentLocationLongitude),
    paymentLocationReference: normalizeProfileText(profile?.paymentLocationReference),
    kioskNativeQrReady: profile?.kioskNativeQrReady === true,
    commissionRate: normalizeCommissionRate(profile?.commissionRate),
    paymentSplitMode: normalizePaymentSplitMode(
      profile?.paymentSplitMode || DEFAULT_BRANCH_PAYMENT_SPLIT_MODE
    ),
    kioskPaymentMethods: normalizePaymentMethods(
      profile?.kioskPaymentMethods || DEFAULT_KIOSK_PAYMENT_METHODS,
      "kiosk"
    ),
    qrPaymentMethods: normalizePaymentMethods(
      profile?.qrPaymentMethods || DEFAULT_QR_PAYMENT_METHODS,
      "qr"
    ),
  };
};

const getPixelDistance = (left, right) =>
  Math.sqrt(
    (left.r - right.r) ** 2 + (left.g - right.g) ** 2 + (left.b - right.b) ** 2
  );

const sampleCornerBackground = (data, width, height) => {
  const corners = [
    [0, 0],
    [width - 1, 0],
    [0, height - 1],
    [width - 1, height - 1],
  ]
    .filter(([x, y]) => x >= 0 && y >= 0)
    .map(([x, y]) => {
      const index = (y * width + x) * 4;
      return {
        r: data[index],
        g: data[index + 1],
        b: data[index + 2],
        a: data[index + 3] / 255,
      };
    });

  if (!corners.length) {
    return null;
  }

  const opaqueCorners = corners.filter((corner) => corner.a > 0.92);

  if (opaqueCorners.length < 3) {
    return null;
  }

  const reference = opaqueCorners[0];
  const allSimilar = opaqueCorners.every(
    (corner) => getPixelDistance(corner, reference) <= 28
  );

  if (!allSimilar) {
    return null;
  }

  const total = opaqueCorners.reduce(
    (acc, corner) => ({
      r: acc.r + corner.r,
      g: acc.g + corner.g,
      b: acc.b + corner.b,
    }),
    { r: 0, g: 0, b: 0 }
  );

  return {
    r: total.r / opaqueCorners.length,
    g: total.g / opaqueCorners.length,
    b: total.b / opaqueCorners.length,
  };
};

const detectBrandAssetBounds = (data, width, height) => {
  const cornerBackground = sampleCornerBackground(data, width, height);
  const padding = 10;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let index = 0; index < data.length; index += 4) {
    const alpha = data[index + 3] / 255;

    if (alpha <= 0.08) {
      continue;
    }

    const pixelIndex = index / 4;
    const x = pixelIndex % width;
    const y = Math.floor(pixelIndex / width);
    const currentPixel = {
      r: data[index],
      g: data[index + 1],
      b: data[index + 2],
    };

    if (
      cornerBackground &&
      alpha >= 0.92 &&
      getPixelDistance(currentPixel, cornerBackground) <= 26
    ) {
      continue;
    }

    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }

  if (maxX < minX || maxY < minY) {
    return {
      cropX: 0,
      cropY: 0,
      cropWidth: width,
      cropHeight: height,
    };
  }

  const cropX = Math.max(0, minX - padding);
  const cropY = Math.max(0, minY - padding);

  return {
    cropX,
    cropY,
    cropWidth: Math.min(width - cropX, maxX - minX + 1 + padding * 2),
    cropHeight: Math.min(height - cropY, maxY - minY + 1 + padding * 2),
  };
};

const resizeImageFile = (file, { profile = "brand" } = {}) => optimizeImageFile(file, {
  profile,
  cropBounds: profile === "brand" ? detectBrandAssetBounds : undefined,
});

const renderLogoPreview = (src, alt, style) =>
  src ? <img src={src} alt={alt} style={style} /> : null;

const renderCoverFrame = ({
  src,
  alt,
  mode,
  wrapStyle,
  imageStyle,
  centeredBackdropStyle,
  centeredOverlayStyle,
  centeredImageStyle,
}) => {
  const normalizedMode = normalizeCoverDisplayMode(mode);

  if (normalizedMode === "centered") {
    return (
      <div style={wrapStyle}>
        <img src={src} alt="" aria-hidden="true" style={centeredBackdropStyle} />
        <div style={centeredOverlayStyle} />
        <img src={src} alt={alt} style={centeredImageStyle} />
      </div>
    );
  }

  return (
    <div style={wrapStyle}>
      <img
        src={src}
        alt={alt}
        style={{
          ...imageStyle,
          objectFit: normalizedMode === "normal" ? "cover" : "fill",
        }}
      />
    </div>
  );
};

const loadingLogoKeyframes = `
  @keyframes mozzoapp-loading-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes mozzoapp-loading-pulse {
    0%, 100% { transform: scale(0.92); opacity: 0.82; }
    50% { transform: scale(1.04); opacity: 1; }
  }

  @keyframes mozzoapp-loading-float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
  }
`;

const initialWaiterForm = {
  fullName: "",
  email: "",
};

function MiRestaurante() {
  const {
    canAccessModule,
    canPerformAction,
    currentAccessRoleLabel,
    currentRestaurant,
    currentCompany,
    user,
    isCreatorMode,
    isSupabaseConfigured,
  } = useAccessControl();
  const showBetaSync = getRuntimeEnvironment() !== "production";
  const [perfil, setPerfil] = useState(() => getRestaurantProfile(currentRestaurant));
  const [estadoGuardado, setEstadoGuardado] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [processingImage, setProcessingImage] = useState(false);
  const imageRequestRef = useRef(null);
  const restaurantId = currentRestaurant?.id;
  useEffect(() => {
    setProcessingImage(false);
    return () => { imageRequestRef.current = null; };
  }, [restaurantId]);
  const restaurantRef = useRef(currentRestaurant);
  restaurantRef.current = currentRestaurant;
  const draftRevisionRef = useRef(0);
  const categoryOrderDirtyRef = useRef(false);
  const subcategoryOrderDirtyRef = useRef(false);
  const savingRequestRef = useRef(null);
  const [estadoMozos, setEstadoMozos] = useState("");
  const [syncingRemoteState, setSyncingRemoteState] = useState(false);
  const [waiterForm, setWaiterForm] = useState(initialWaiterForm);
  const [branchWaiters, setBranchWaiters] = useState([]);
  const [branchInvitations, setBranchInvitations] = useState([]);
  const [loadingWaiters, setLoadingWaiters] = useState(false);
  const mergeRemoteProfileIntoDraft = useCallback(
    (baseProfile, remoteProfile) =>
      mergeRemoteBranchIntoProfilePreservingLocalPreferences(
        baseProfile,
        remoteProfile
      ),
    []
  );
  const buildRemoteSyncPayload = useCallback(
    (sourceProfile) =>
      buildBranchPublicProfileSyncPayload(
        sourceProfile,
        buildRestaurantBrandTheme(sourceProfile),
        categoryOrderDirtyRef.current,
        subcategoryOrderDirtyRef.current
      ),
    []
  );

  useEffect(() => {
    const restaurant = restaurantRef.current;
    const localProfile = getRestaurantProfile(restaurant);
    const loadRevision = ++draftRevisionRef.current;
    categoryOrderDirtyRef.current = false;
    subcategoryOrderDirtyRef.current = false;
    savingRequestRef.current = null;
    setSavingProfile(false);
    setSyncingRemoteState(false);
    setPerfil(localProfile);
    setEstadoGuardado("");
    setEstadoMozos("");

    let active = true;

    if (!restaurantId || isCreatorMode || !isSupabaseConfigured) {
      return () => {
        active = false;
      };
    }

    const loadRemoteProfile = async () => {
      try {
        const remoteProfile = await getBranchPublicProfile(restaurantId);

        if (!active || draftRevisionRef.current !== loadRevision) {
          return;
        }

        // Entrar al editor solo lee: una copia vieja de la sesion no debe
        // reemplazar la configuracion ya guardada en la sucursal.
        const mergedProfile = saveRestaurantProfile(
          restaurant,
          mergeRemoteProfileIntoDraft(localProfile, remoteProfile)
        );

        setPerfil(mergedProfile);
      } catch (error) {
        if (!active || draftRevisionRef.current !== loadRevision) {
          return;
        }

        console.error("No pudimos sincronizar el perfil publico de la sucursal:", error);
        setEstadoGuardado(
          error.message ||
            "No pudimos cargar la configuracion guardada de la sucursal."
        );
      }
    };

    loadRemoteProfile();

    return () => {
      active = false;
      savingRequestRef.current = null;
    };
  }, [
    restaurantId,
    isCreatorMode,
    isSupabaseConfigured,
    mergeRemoteProfileIntoDraft,
  ]);

  useEffect(() => {
    let active = true;

    const loadWaiters = async () => {
      if (
        isCreatorMode ||
        !isSupabaseConfigured ||
        !currentCompany?.id ||
        !currentRestaurant?.id
      ) {
        if (active) {
          setBranchWaiters([]);
          setBranchInvitations([]);
        }
        return;
      }

      setLoadingWaiters(true);
      setEstadoMozos("");

      try {
        const [nextWaiters, nextInvitations] = await Promise.all([
          listBranchWaiters(currentCompany.id, currentRestaurant.id),
          listCompanyInvitations(currentCompany.id),
        ]);

        if (!active) {
          return;
        }

        setBranchWaiters(nextWaiters);
        setBranchInvitations(
          nextInvitations.filter(
            (invitation) =>
              invitation.branch_id === currentRestaurant.id &&
              invitation.workspace_role === "mozo"
          )
        );
      } catch (error) {
        if (!active) {
          return;
        }

        setEstadoMozos(error.message || "No se pudo cargar el equipo de salon.");
      } finally {
        if (active) {
          setLoadingWaiters(false);
        }
      }
    };

    loadWaiters();

    return () => {
      active = false;
    };
  }, [
    currentCompany?.id,
    currentRestaurant?.id,
    isCreatorMode,
    isSupabaseConfigured,
  ]);

  const nombreMostrado = useMemo(
    () => perfil.nombre || currentRestaurant?.name || "MozzoApp",
    [currentRestaurant?.name, perfil.nombre]
  );

  const horarioResumen = useMemo(
    () => buildHorarioResumen(perfil.horarioDetallado || createDefaultHorarioDetallado()),
    [perfil.horarioDetallado]
  );

  const pendingWaiterInvitations = useMemo(
    () => branchInvitations.filter((invitation) => invitation.status === "pending"),
    [branchInvitations]
  );

  const activeWaitersCount = useMemo(
    () => branchWaiters.filter((waiter) => waiter.branchAccessActive).length,
    [branchWaiters]
  );
  const brandTheme = useMemo(() => buildRestaurantBrandTheme(perfil), [perfil]);
  const selectedLoadingAnimation =
    BRAND_LOADING_ANIMATION_OPTIONS.find(
      (option) => option.id === (perfil.branding?.loadingAnimation || "spin")
    ) || BRAND_LOADING_ANIMATION_OPTIONS[0];
  const effectiveServiceMode = useMemo(
    () => normalizeServiceMode(perfil.serviceMode || currentRestaurant?.serviceMode),
    [currentRestaurant?.serviceMode, perfil.serviceMode]
  );
  const isKioskBranch = effectiveServiceMode === "kiosk";
  const canSeeRestaurantModule = canAccessModule("restaurante");
  const canManageRestaurantSettings =
    canSeeRestaurantModule && canPerformAction("manage_settings");
  const canManageRestaurantUsers =
    canSeeRestaurantModule && canPerformAction("manage_users");
  const restaurantSettingsDisabled = !canManageRestaurantSettings || processingImage;
  const restaurantUsersDisabled = !canManageRestaurantUsers;
  const waiterFeatureEnabled = normalizeOperationalFeatureEnabled(perfil.waiterEnabled);

  const notifyRestaurantPermissionDenied = useCallback(
    (actionLabel) => {
      const message = `${currentAccessRoleLabel} puede revisar Mi Restaurante, pero no ${actionLabel} en esta sucursal.`;
      setEstadoGuardado(message);
      return false;
    },
    [currentAccessRoleLabel]
  );

  const notifyRestaurantUsersPermissionDenied = useCallback(
    (actionLabel) => {
      const message = `${currentAccessRoleLabel} puede ver el equipo de salon, pero no ${actionLabel} en esta sucursal.`;
      setEstadoMozos(message);
      return false;
    },
    [currentAccessRoleLabel]
  );

  const ensureRestaurantSettingsAccess = useCallback(
    (actionLabel) => {
      if (canManageRestaurantSettings) {
        return true;
      }

      return notifyRestaurantPermissionDenied(actionLabel);
    },
    [canManageRestaurantSettings, notifyRestaurantPermissionDenied]
  );

  const ensureRestaurantUsersAccess = useCallback(
    (actionLabel) => {
      if (canManageRestaurantUsers) {
        return true;
      }

      return notifyRestaurantUsersPermissionDenied(actionLabel);
    },
    [canManageRestaurantUsers, notifyRestaurantUsersPermissionDenied]
  );

  const updatePerfil = (
    updater,
    actionLabel = "editar la identidad visible del restaurante"
  ) => {
    if (!ensureRestaurantSettingsAccess(actionLabel)) {
      return false;
    }

    draftRevisionRef.current += 1;
    setEstadoGuardado("Cambios sin guardar. Presiona Guardar cambios para aplicarlos.");
    setPerfil((prev) => {
      const nextProfile =
        typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      const nextWithHorario = {
        ...nextProfile,
        horario: buildHorarioResumen(
          nextProfile.horarioDetallado || createDefaultHorarioDetallado()
        ),
      };

      emitRestaurantProfileUpdate(currentRestaurant, nextWithHorario);
      return nextWithHorario;
    });

    return true;
  };

  const persistPerfilDraft = (
    updater,
    successMessage,
    actionLabel = "aplicar cambios de configuracion"
  ) => {
    if (!ensureRestaurantSettingsAccess(actionLabel)) {
      return null;
    }

    draftRevisionRef.current += 1;
    try {
      const nextProfile =
        typeof updater === "function" ? updater(perfil) : { ...perfil, ...updater };
      const persistedProfile = saveRestaurantProfile(currentRestaurant, {
        ...nextProfile,
        horario: buildHorarioResumen(
          nextProfile.horarioDetallado || createDefaultHorarioDetallado()
        ),
      });

      setPerfil(persistedProfile);

      if (successMessage) {
        setEstadoGuardado(successMessage);
      }

      return persistedProfile;
    } catch (error) {
      setEstadoGuardado(error.message || "No se pudo aplicar el cambio.");
      return null;
    }
  };

  const updateHorario = (dayId, updater) => {
    updatePerfil((prev) => {
      const nextHorario = (prev.horarioDetallado || createDefaultHorarioDetallado()).map((day) =>
        day.id === dayId ? updater(day) : day
      );

      return {
        ...prev,
        horarioDetallado: nextHorario,
      };
    }, "modificar los horarios");
  };

  const toggleDiaAbierto = (dayId, abierto) => {
    updateHorario(dayId, (day) => ({
      ...day,
      abierto,
      franjas: abierto ? day.franjas?.length ? day.franjas : [createShift("08:00", "00:00")] : [],
    }));
  };

  const updateFranja = (dayId, franjaIndex, field, value) => {
    updateHorario(dayId, (day) => ({
      ...day,
      franjas: day.franjas.map((franja, index) =>
        index === franjaIndex ? { ...franja, [field]: value } : franja
      ),
    }));
  };

  const addFranja = (dayId) => {
    updateHorario(dayId, (day) => {
      if (!day.abierto || day.franjas.length >= 2) {
        return day;
      }

      return {
        ...day,
        franjas: [...day.franjas, createShift()],
      };
    });
  };

  const removeFranja = (dayId, franjaIndex) => {
    updateHorario(dayId, (day) => {
      if (!day.abierto || day.franjas.length <= 1) {
        return day;
      }

      return {
        ...day,
        franjas: day.franjas.filter((_, index) => index !== franjaIndex),
      };
    });
  };

  const toggleCaracteristica = (item) => {
    updatePerfil((prev) => {
      const actuales = prev.caracteristicas || [];
      const existe = actuales.includes(item);

      return {
        ...prev,
        caracteristicas: existe
          ? actuales.filter((value) => value !== item)
          : [...actuales, item],
      };
    }, "cambiar las caracteristicas del restaurante");
  };

  const handleSave = async () => {
    if (imageRequestRef.current || savingRequestRef.current || !ensureRestaurantSettingsAccess("guardar la configuracion del restaurante")) {
      return;
    }

    const restaurant = currentRestaurant;
    const saveRevision = ++draftRevisionRef.current;
    const request = {};
    savingRequestRef.current = request;
    setSavingProfile(true);
    setEstadoGuardado("Guardando cambios...");
    try {
      const draftProfile = {
        ...perfil,
        horario: horarioResumen,
      };

      let nextProfile = draftProfile;

      if (!isCreatorMode && isSupabaseConfigured && restaurant?.id) {
        const syncedProfile = await updateBranchPublicProfile(
          restaurant.id,
          buildRemoteSyncPayload(draftProfile)
        );

        nextProfile = saveRestaurantProfile(
          restaurant,
          mergeRemoteProfileIntoDraft(draftProfile, syncedProfile)
        );
      } else {
        nextProfile = saveRestaurantProfile(restaurant, draftProfile);
      }

      if (savingRequestRef.current !== request) return;
      if (draftRevisionRef.current === saveRevision) {
        setPerfil(nextProfile);
        setEstadoGuardado(!isCreatorMode && isSupabaseConfigured && restaurant?.id
          ? "Cambios guardados y sincronizados con la sucursal."
          : "Cambios guardados.");
      } else {
        setEstadoGuardado("Se guardaron los cambios anteriores. Hay nuevos cambios sin guardar.");
      }
    } catch (error) {
      if (savingRequestRef.current === request) {
        setEstadoGuardado(
          error.message || "No se pudo guardar y sincronizar la informacion del restaurante."
        );
      }
    } finally {
      if (savingRequestRef.current === request) {
        savingRequestRef.current = null;
        setSavingProfile(false);
      }
    }
  };

  const handleSyncBranchToSupabase = async () => {
    if (savingRequestRef.current || !ensureRestaurantSettingsAccess("sincronizar la sucursal con beta")) {
      return;
    }

    if (isCreatorMode || !isSupabaseConfigured || !currentRestaurant?.id) {
      setEstadoGuardado(
        "Esta accion necesita una sucursal real conectada a Supabase."
      );
      return;
    }

    const saveRevision = ++draftRevisionRef.current;
    const request = {};
    savingRequestRef.current = request;
    setSyncingRemoteState(true);

    try {
      const freshestLocalProfile = { ...perfil, horario: horarioResumen };
      const syncedProfile = await updateBranchPublicProfile(
        currentRestaurant.id,
        buildRemoteSyncPayload(freshestLocalProfile)
      );
      const mergedProfile = saveRestaurantProfile(
        currentRestaurant,
        mergeRemoteProfileIntoDraft(freshestLocalProfile, syncedProfile)
      );
      const localPromotions = getPromotions(currentRestaurant);

      await saveBranchPromotions(currentRestaurant.id, localPromotions);

      if (savingRequestRef.current !== request) return;
      if (draftRevisionRef.current === saveRevision) {
        setPerfil(mergedProfile);
        setEstadoGuardado(
          "Sincronizamos la sucursal con Supabase. Beta ya puede leer el perfil y los combos/promos actualizados."
        );
      } else {
        setEstadoGuardado("Se guardaron los cambios anteriores. Hay nuevos cambios sin guardar.");
      }
    } catch (error) {
      if (savingRequestRef.current === request) {
        setEstadoGuardado(
          error.message ||
            "No pudimos empujar toda la sucursal a Supabase en este momento."
        );
      }
    } finally {
      if (savingRequestRef.current === request) {
        savingRequestRef.current = null;
        setSyncingRemoteState(false);
      }
    }
  };


  const beginImageUpload = () => {
    if (imageRequestRef.current || savingRequestRef.current) return null;
    const request = {};
    imageRequestRef.current = request;
    setProcessingImage(true);
    setEstadoGuardado("Optimizando imagen...");
    return request;
  };

  const finishImageUpload = (request) => {
    if (imageRequestRef.current !== request) return;
    imageRequestRef.current = null;
    setProcessingImage(false);
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];

    if (!ensureRestaurantSettingsAccess("cambiar la portada del restaurante")) {
      event.target.value = "";
      return;
    }

    if (!file) {
      return;
    }

    const request = beginImageUpload();
    if (!request) return;
    try {
      const imageDataUrl = await resizeImageFile(file, { profile: "cover" });
      if (imageRequestRef.current !== request) return;
      updatePerfil((prev) => ({
        ...prev,
        imagenPortada: imageDataUrl,
        // Mantiene ambos campos alineados para que una URL remota vieja no gane al guardar.
        coverUrl: imageDataUrl,
      }));
      setEstadoGuardado("Portada cargada. No olvides guardar los cambios.");
    } catch (error) {
      if (imageRequestRef.current === request) setEstadoGuardado(error.message || "No se pudo cargar la imagen.");
    } finally {
      finishImageUpload(request);
      event.target.value = "";
    }
  };

  const handleBrandAssetChange = async (field, event) => {
    const file = event.target.files?.[0];

    if (!ensureRestaurantSettingsAccess("cambiar logos e imagenes de marca")) {
      event.target.value = "";
      return;
    }

    if (!file) {
      return;
    }

    const request = beginImageUpload();
    if (!request) return;
    try {
      const imageDataUrl = await resizeImageFile(file);
      if (imageRequestRef.current !== request) return;

      updatePerfil((prev) => ({
        ...prev,
        branding: {
          ...(prev.branding || {}),
          [field]: imageDataUrl,
        },
      }));

      if (field === "logoPrimaryUrl") {
        const palette = await extractBrandPaletteFromImage(imageDataUrl);
        if (imageRequestRef.current !== request) return;
        updatePerfil((prev) => ({
          ...prev,
          branding: {
            ...(prev.branding || {}),
            logoPrimaryUrl: imageDataUrl,
            suggestedPrimaryColor: palette.primaryColor,
            suggestedSecondaryColor: palette.secondaryColor,
            primaryColor: palette.primaryColor,
            secondaryColor: palette.secondaryColor,
          },
        }));
        setEstadoGuardado(
          "Logo cargado. Te sugerimos colores automaticamente; puedes ajustarlos abajo."
        );
      } else {
        setEstadoGuardado("Imagen de marca cargada. No olvides guardar los cambios.");
      }
    } catch (error) {
      if (imageRequestRef.current === request) setEstadoGuardado(error.message || "No se pudo cargar la imagen de marca.");
    } finally {
      finishImageUpload(request);
      event.target.value = "";
    }
  };

  const handleKioskChoiceImageChange = async (field, event) => {
    const file = event.target.files?.[0];

    if (!ensureRestaurantSettingsAccess("cambiar las imagenes del ingreso kiosk")) {
      event.target.value = "";
      return;
    }

    if (!file) {
      return;
    }

    const request = beginImageUpload();
    if (!request) return;
    try {
      const imageDataUrl = await resizeImageFile(file, { profile: "kiosk" });
      if (imageRequestRef.current !== request) return;

      const draftProfile = {
        ...perfil,
        [field]: imageDataUrl,
        horario: buildHorarioResumen(
          perfil.horarioDetallado || createDefaultHorarioDetallado()
        ),
      };
      const localProfile = saveRestaurantProfile(currentRestaurant, draftProfile);

      setPerfil(localProfile);
      emitRestaurantProfileUpdate(currentRestaurant, localProfile);

      if (!isCreatorMode && isSupabaseConfigured && currentRestaurant?.id) {
        try {
          const syncedProfile = await updateBranchPublicProfile(
            currentRestaurant.id,
            buildRemoteSyncPayload(localProfile)
          );
          if (imageRequestRef.current !== request) return;
          const mergedProfile = saveRestaurantProfile(
            currentRestaurant,
            mergeRemoteProfileIntoDraft(localProfile, syncedProfile)
          );

          setPerfil(mergedProfile);
          emitRestaurantProfileUpdate(currentRestaurant, mergedProfile);
          setEstadoGuardado(
            "Imagen del ingreso kiosk guardada y sincronizada con el totem."
          );
          return;
        } catch (syncError) {
          if (imageRequestRef.current !== request) return;
          console.error("No pudimos sincronizar la imagen del ingreso kiosk:", syncError);
          setEstadoGuardado(
            syncError.message ||
              "Imagen cargada en este equipo, pero no pudimos sincronizarla con beta."
          );
          return;
        }
      }

      setEstadoGuardado("Imagen del ingreso kiosk guardada en este equipo.");
    } catch (error) {
      if (imageRequestRef.current === request) setEstadoGuardado(error.message || "No se pudo cargar la imagen del kiosk.");
    } finally {
      finishImageUpload(request);
      event.target.value = "";
    }
  };

  const reloadWaiters = async () => {
    if (
      isCreatorMode ||
      !isSupabaseConfigured ||
      !currentCompany?.id ||
      !currentRestaurant?.id
    ) {
      setBranchWaiters([]);
      setBranchInvitations([]);
      return;
    }

    const [nextWaiters, nextInvitations] = await Promise.all([
      listBranchWaiters(currentCompany.id, currentRestaurant.id),
      listCompanyInvitations(currentCompany.id),
    ]);

    setBranchWaiters(nextWaiters);
    setBranchInvitations(
      nextInvitations.filter(
        (invitation) =>
          invitation.branch_id === currentRestaurant.id &&
          invitation.workspace_role === "mozo"
      )
    );
  };

  const handleCreateWaiter = async (event) => {
    event.preventDefault();
    setEstadoMozos("");

    if (!ensureRestaurantUsersAccess("sumar mozos")) {
      return;
    }

    if (!waiterFeatureEnabled) {
      setEstadoMozos(
        "La operacion con mozos esta desactivada para esta sucursal. Activa el switch de mozo para volver a invitar o habilitar salon."
      );
      return;
    }

    if (isCreatorMode || !isSupabaseConfigured) {
      setEstadoMozos("Para invitar mozos hace falta entrar con una cuenta real conectada a Supabase.");
      return;
    }

    if (!currentCompany?.id || !currentRestaurant?.id) {
      setEstadoMozos("No encontramos la sucursal activa para sumar un mozo.");
      return;
    }

    setLoadingWaiters(true);

    try {
      const created = await createWaiterInvitation(currentCompany.id, user?.id || "", {
        branchId: currentRestaurant.id,
        fullName: waiterForm.fullName,
        email: waiterForm.email,
      });
      setWaiterForm(initialWaiterForm);
      await reloadWaiters();
      setEstadoMozos(
        created.invitation_email_sent === false
          ? created.invitation_email_error ||
              "El mozo quedó invitado, pero no se pudo enviar el correo de acceso."
          : "Mozo agregado y correo de acceso enviado correctamente."
      );
    } catch (error) {
      setEstadoMozos(error.message || "No se pudo agregar el mozo.");
    } finally {
      setLoadingWaiters(false);
    }
  };

  const handleToggleWaiter = async (waiter) => {
    if (!ensureRestaurantUsersAccess("activar o pausar mozos")) {
      return;
    }

    if (!waiterFeatureEnabled) {
      setEstadoMozos(
        "La operacion con mozos esta desactivada para esta sucursal. Vuelve a activarla primero si quieres retomar el equipo de salon."
      );
      return;
    }

    setEstadoMozos("");
    setLoadingWaiters(true);

    try {
      await updateWaiterBranchAccess(waiter.branchId, waiter.userId, !waiter.branchAccessActive);
      await reloadWaiters();
      setEstadoMozos(
        waiter.branchAccessActive
          ? "Mozo pausado para esta sucursal."
          : "Mozo activado para operar en esta sucursal."
      );
    } catch (error) {
      setEstadoMozos(error.message || "No se pudo cambiar el estado del mozo.");
    } finally {
      setLoadingWaiters(false);
    }
  };

  return (
    <div style={{ ...styles.page, fontFamily: brandTheme.fontFamily }}>
      <style>{loadingLogoKeyframes}</style>
      <div style={styles.header}>
        <div>
          <div style={{ ...styles.overline, color: brandTheme.primaryColor }}>
            Mi Restaurante
          </div>
          <h1 style={styles.title}>{nombreMostrado}</h1>
          <p style={styles.subtitle}>
            Aqui defines la identidad del local: como se presenta, que transmite y que
            informacion vera el comensal.
          </p>
        </div>

        <button
          type="button"
          style={{
            ...styles.primaryButton,
            backgroundColor: brandTheme.primaryColor,
            color: brandTheme.secondaryColor,
          }}
          onClick={handleSave}
          disabled={restaurantSettingsDisabled || savingProfile || syncingRemoteState}
        >
          {savingProfile ? "Guardando..." : "Guardar cambios"}
        </button>
        {showBetaSync && !isCreatorMode && isSupabaseConfigured ? (
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={handleSyncBranchToSupabase}
            disabled={syncingRemoteState || savingProfile || restaurantSettingsDisabled}
          >
            {syncingRemoteState ? "Sincronizando..." : "Sincronizar con beta"}
          </button>
        ) : null}
      </div>

      {estadoGuardado && (
        <div
          style={{
            ...styles.notice,
            ...(estadoGuardado.includes("No se pudo") ? styles.noticeError : styles.noticeOk),
          }}
        >
          {estadoGuardado}
        </div>
      )}

      {!canManageRestaurantSettings ? (
        <div style={{ ...styles.notice, ...styles.noticeError }}>
          {currentAccessRoleLabel} puede consultar la identidad del restaurante, pero no
          editar datos visibles, imagenes, portada ni horarios en esta sucursal.
        </div>
      ) : null}

      <section style={styles.grid}>
        <article style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>Marca y estilo</h2>
              <span style={styles.helperText}>
                Cargas tus logos, te sugerimos colores a partir del principal y eliges una
                tipografia. El sistema mantiene la estructura prolija y solo cambia la
                identidad visual.
              </span>
            </div>
            <span
              style={{
                ...styles.scheduleSummary,
                backgroundColor: brandTheme.softColor,
                color: brandTheme.primaryColor,
              }}
            >
              Tema base listo
            </span>
          </div>

          <div style={styles.brandAssetGrid}>
            <label style={styles.brandAssetCard}>
              <span style={styles.label}>Logo principal</span>
              <input
                type="file"
                accept="image/*"
                onChange={(event) => handleBrandAssetChange("logoPrimaryUrl", event)}
                style={styles.input}
                disabled={restaurantSettingsDisabled}
              />
              <div style={styles.brandAssetPreview}>
                {renderLogoPreview(
                  perfil.branding?.logoPrimaryUrl,
                  `Logo principal de ${nombreMostrado}`,
                  styles.logoPrimaryPreview
                ) || <span style={styles.brandAssetPlaceholder}>Subir logo</span>}
              </div>
            </label>

            <label style={styles.brandAssetCard}>
              <span style={styles.label}>Mini logo</span>
              <input
                type="file"
                accept="image/*"
                onChange={(event) => handleBrandAssetChange("logoCompactUrl", event)}
                style={styles.input}
                disabled={restaurantSettingsDisabled}
              />
              <div style={styles.brandAssetPreview}>
                {renderLogoPreview(
                  brandTheme.effectiveLogoCompactUrl,
                  `Mini logo de ${nombreMostrado}`,
                  styles.logoCompactPreview
                ) || <span style={styles.brandAssetPlaceholder}>Usa el logo principal</span>}
              </div>
              <span style={styles.brandAssetHint}>Opcional. Si no cargas uno, hereda el principal.</span>
            </label>

            <label style={styles.brandAssetCard}>
              <span style={styles.label}>Icono</span>
              <input
                type="file"
                accept="image/*"
                onChange={(event) => handleBrandAssetChange("iconLogoUrl", event)}
                style={styles.input}
                disabled={restaurantSettingsDisabled}
              />
              <div style={styles.brandAssetPreview}>
                {renderLogoPreview(
                  brandTheme.effectiveIconLogoUrl,
                  `Icono de ${nombreMostrado}`,
                  styles.logoIconPreview
                ) || <span style={styles.brandAssetPlaceholder}>Usa el logo principal</span>}
              </div>
              <span style={styles.brandAssetHint}>
                Ideal para la pestana del navegador. Si puedes, sube una version cuadrada.
              </span>
            </label>

            <label style={styles.brandAssetCard}>
              <span style={styles.label}>Logo de carga</span>
              <input
                type="file"
                accept="image/*"
                onChange={(event) => handleBrandAssetChange("loadingLogoUrl", event)}
                style={styles.input}
                disabled={restaurantSettingsDisabled}
              />
              <div style={styles.brandAssetPreview}>
                {renderLogoPreview(
                  brandTheme.effectiveLoadingLogoUrl,
                  `Logo de carga de ${nombreMostrado}`,
                  styles.logoCompactPreview
                ) || <span style={styles.brandAssetPlaceholder}>Usa el logo principal</span>}
              </div>
              <span style={styles.brandAssetHint}>Opcional. Si no cargas uno, hereda el principal.</span>
            </label>
          </div>

          <div style={styles.brandControlsGrid}>
            <label style={styles.field}>
              <span style={styles.label}>Color principal</span>
              <div style={styles.colorControlRow}>
                <input
                  type="color"
                  value={brandTheme.primaryColor}
                  onChange={(event) =>
                    updatePerfil((prev) => ({
                      ...prev,
                      branding: {
                        ...(prev.branding || {}),
                        primaryColor: event.target.value,
                      },
                    }))
                  }
                  style={styles.colorInput}
                  disabled={restaurantSettingsDisabled}
                />
                <input
                  type="text"
                  value={brandTheme.primaryColor}
                  onChange={(event) =>
                    updatePerfil((prev) => ({
                      ...prev,
                      branding: {
                        ...(prev.branding || {}),
                        primaryColor: event.target.value,
                      },
                    }))
                  }
                  style={styles.input}
                  disabled={restaurantSettingsDisabled}
                />
              </div>
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Color de contraste</span>
              <div style={styles.colorControlRow}>
                <input
                  type="color"
                  value={brandTheme.secondaryColor}
                  onChange={(event) =>
                    updatePerfil((prev) => ({
                      ...prev,
                      branding: {
                        ...(prev.branding || {}),
                        secondaryColor: event.target.value,
                      },
                    }))
                  }
                  style={styles.colorInput}
                  disabled={restaurantSettingsDisabled}
                />
                <input
                  type="text"
                  value={brandTheme.secondaryColor}
                  onChange={(event) =>
                    updatePerfil((prev) => ({
                      ...prev,
                      branding: {
                        ...(prev.branding || {}),
                        secondaryColor: event.target.value,
                      },
                    }))
                  }
                  style={styles.input}
                  disabled={restaurantSettingsDisabled}
                />
              </div>
            </label>
          </div>

          <div style={styles.suggestedPaletteRow}>
            <span style={styles.suggestedPaletteLabel}>Sugerido por el logo</span>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() =>
                updatePerfil((prev) => ({
                  ...prev,
                  branding: {
                    ...(prev.branding || {}),
                    primaryColor:
                      prev.branding?.suggestedPrimaryColor || brandTheme.primaryColor,
                    secondaryColor:
                      prev.branding?.suggestedSecondaryColor || brandTheme.secondaryColor,
                  },
                }), "aplicar la paleta sugerida por el logo")
              }
              disabled={restaurantSettingsDisabled}
            >
              Usar sugerencia
            </button>
            <div style={styles.suggestedSwatches}>
              <span
                style={{
                  ...styles.suggestedSwatch,
                  backgroundColor:
                    perfil.branding?.suggestedPrimaryColor || brandTheme.primaryColor,
                }}
              />
              <span
                style={{
                  ...styles.suggestedSwatch,
                  backgroundColor:
                    perfil.branding?.suggestedSecondaryColor || brandTheme.secondaryColor,
                }}
              />
            </div>
          </div>

          <div style={styles.fontOptionsWrap}>
            {BRAND_FONT_OPTIONS.map((option) => {
              const active = brandTheme.fontPreset === option.id;

              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() =>
                    updatePerfil((prev) => ({
                      ...prev,
                      branding: {
                        ...(prev.branding || {}),
                        fontPreset: option.id,
                      },
                    }))
                  }
                  style={{
                    ...styles.fontOptionButton,
                    ...(active ? styles.fontOptionButtonActive : {}),
                    fontFamily: option.family,
                    borderColor: active ? brandTheme.primaryColor : "#dbe2ea",
                    color: active ? brandTheme.primaryColor : "#334155",
                    backgroundColor: active ? brandTheme.softColor : "#ffffff",
                  }}
                  disabled={restaurantSettingsDisabled}
                >
                  <strong>{option.label}</strong>
                  <span style={styles.fontOptionSample}>Tu marca se vera asi</span>
                </button>
              );
            })}
          </div>

          <div style={styles.loadingBrandRow}>
            <label style={{ ...styles.field, minWidth: 220 }}>
              <span style={styles.label}>Movimiento del logo de carga</span>
              <select
                value={perfil.branding?.loadingAnimation || "spin"}
                onChange={(event) =>
                  updatePerfil((prev) => ({
                    ...prev,
                    branding: {
                      ...(prev.branding || {}),
                      loadingAnimation: event.target.value,
                    },
                  }))
                }
                style={styles.input}
                disabled={restaurantSettingsDisabled}
              >
                {BRAND_LOADING_ANIMATION_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </article>

        <div style={styles.gridSideColumn}>
          <article style={styles.panel}>
            <h2 style={styles.panelTitle}>Estilo del menu QR</h2>
            <p>Asi vera la carta el cliente cuando escanee el QR de esta sucursal.</p>
            <QrMenuStyleEditor key={restaurantId} branchId={restaurantId}
              useRemote={!isCreatorMode && isSupabaseConfigured}
              value={perfil.qrMenuStyle} categoryOrder={perfil.qrCategoryOrder}
              subcategoryOrder={perfil.qrSubcategoryOrder}
              onSubcategoryOrderChange={(qrSubcategoryOrder) => {
                subcategoryOrderDirtyRef.current = true;
                updatePerfil((prev) => ({ ...prev, qrSubcategoryOrder }));
              }}
              primaryColor={brandTheme.primaryColor} disabled={restaurantSettingsDisabled}
              onStyleChange={(qrMenuStyle) => updatePerfil((prev) => ({ ...prev, qrMenuStyle }))}
              onOrderChange={(qrCategoryOrder) => {
                categoryOrderDirtyRef.current = true;
                updatePerfil((prev) => ({ ...prev, qrCategoryOrder }));
              }} />
            <p>Usa las fotos de tus categorias y platos, junto con los colores de tu restaurante.</p>
          </article>
          <article style={styles.panel}>
            <h2 style={styles.panelTitle}>Datos visibles del restaurante</h2>
            <div style={styles.fieldGrid}>
              <label style={styles.field}>
                <span style={styles.label}>Nombre del restaurante</span>
                <input
                  type="text"
                  value={perfil.nombre}
                  onChange={(event) =>
                    updatePerfil((prev) => ({ ...prev, nombre: event.target.value }))
                  }
                  style={styles.input}
                  disabled={restaurantSettingsDisabled}
                />
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Telefono</span>
                <input
                  type="text"
                  value={perfil.telefono}
                  onChange={(event) =>
                    updatePerfil((prev) => ({ ...prev, telefono: event.target.value }))
                  }
                  style={styles.input}
                  disabled={restaurantSettingsDisabled}
                />
              </label>

              <label style={{ ...styles.field, gridColumn: "1 / -1" }}>
                <span style={styles.label}>Direccion</span>
                <input
                  type="text"
                  value={perfil.direccion}
                  onChange={(event) =>
                    updatePerfil((prev) => ({ ...prev, direccion: event.target.value }))
                  }
                  style={styles.input}
                  disabled={restaurantSettingsDisabled}
                />
              </label>
            </div>
          </article>

          <article style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={{ ...styles.panelTitle, marginBottom: "8px" }}>
                  Vista previa de carga
                </h2>
                <span style={styles.helperText}>
                  Asi se vera el logo mientras el menu termina de abrir en el celular o en
                  el kiosk.
                </span>
              </div>
              <span style={styles.loadingPreviewBadge}>
                Movimiento: {selectedLoadingAnimation.label}
              </span>
            </div>

            <div style={styles.loadingPreviewStageLarge}>
              {brandTheme.effectiveLoadingLogoUrl ? (
                <img
                  src={brandTheme.effectiveLoadingLogoUrl}
                  alt={`Vista previa del logo de carga de ${nombreMostrado}`}
                  style={{
                    ...styles.loadingPreviewLogoLarge,
                    ...(perfil.branding?.loadingAnimation === "pulse"
                      ? styles.loadingLogoPulse
                      : perfil.branding?.loadingAnimation === "float"
                        ? styles.loadingLogoFloat
                        : styles.loadingLogoSpin),
                  }}
                />
              ) : (
                <div style={styles.loadingPreviewFallbackLarge}>
                  Sube un logo de carga para previsualizar la animacion.
                </div>
              )}
            </div>

            <span style={styles.loadingPreviewCaption}>
              El movimiento se cambia desde "Marca y estilo" y aqui lo ves aplicado en
              tiempo real.
            </span>
          </article>
        </div>

        <article style={{ ...styles.panel, ...styles.gridFullWidth }}>
          <h2 style={styles.panelTitle}>Presentacion del local</h2>
          <label style={styles.field}>
            <span style={styles.label}>Imagen de portada</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={styles.input}
              disabled={restaurantSettingsDisabled}
            />
          </label>

          <div style={styles.field}>
            <span style={styles.label}>Como se ajusta la imagen</span>
            <div style={styles.coverModeRow}>
              {COVER_DISPLAY_MODE_OPTIONS.map((option) => {
                const active =
                  normalizeCoverDisplayMode(perfil.coverDisplayMode) === option.id;

                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() =>
                      persistPerfilDraft(
                        (prev) => ({
                          ...prev,
                          coverDisplayMode: option.id,
                        }),
                        "Modo de portada aplicado. No olvides guardar para sincronizarlo.",
                        "cambiar la forma de mostrar la portada"
                      )
                    }
                    style={{
                      ...styles.coverModeButton,
                      ...(active ? styles.coverModeButtonActive : {}),
                    }}
                    title={option.description}
                    disabled={restaurantSettingsDisabled}
                  >
                    <strong>{option.label}</strong>
                    <span style={styles.coverModeHint}>{option.description}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {perfil.imagenPortada ? (
            renderCoverFrame({
              src: perfil.imagenPortada,
              alt: `Portada de ${nombreMostrado}`,
              mode: perfil.coverDisplayMode,
              wrapStyle: styles.coverPreviewWrap,
              imageStyle: styles.coverPreview,
              centeredBackdropStyle: styles.coverPreviewCenteredBackdrop,
              centeredOverlayStyle: styles.coverPreviewCenteredOverlay,
              centeredImageStyle: styles.coverPreviewCenteredImage,
            })
          ) : (
            <div style={styles.coverPlaceholder}>
              Carga una portada para que tambien se vea al abrir el menu QR.
            </div>
          )}

          <label style={styles.field}>
            <span style={styles.label}>Descripcion corta</span>
            <textarea
              value={perfil.descripcion}
              onChange={(event) =>
                updatePerfil((prev) => ({ ...prev, descripcion: event.target.value }))
              }
              style={styles.textarea}
              disabled={restaurantSettingsDisabled}
            />
          </label>

          <label style={styles.field}>
            <span style={styles.label}>Mensaje de bienvenida</span>
            <textarea
              value={perfil.mensajeBienvenida}
              onChange={(event) =>
                updatePerfil((prev) => ({ ...prev, mensajeBienvenida: event.target.value }))
              }
              style={{ ...styles.textarea, minHeight: "110px" }}
              disabled={restaurantSettingsDisabled}
            />
          </label>
        </article>

        {isKioskBranch ? (
          <article style={{ ...styles.panel, ...styles.gridFullWidth }}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>Imagenes del ingreso kiosk</h2>
                <span style={styles.helperText}>
                  Estas imagenes aparecen en los boxes de "Comer aqui" y "Para llevar".
                  El logo institucional de arriba va fijo por MozzoApp y no se toca desde
                  el restaurante.
                </span>
              </div>
            </div>

            <div style={styles.kioskChoiceAssetGrid}>
              <label style={styles.brandAssetCard}>
                <span style={styles.label}>Box "Comer aqui"</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) =>
                    handleKioskChoiceImageChange("kioskDineInImageUrl", event)
                  }
                  style={styles.input}
                  disabled={restaurantSettingsDisabled}
                />
                <div style={styles.kioskChoiceAssetPreviewWrap}>
                  {perfil.kioskDineInImageUrl ? (
                    <img
                      src={perfil.kioskDineInImageUrl}
                      alt={`Imagen de Comer aqui para ${nombreMostrado}`}
                      style={styles.kioskChoiceAssetPreviewImage}
                    />
                  ) : (
                    <span style={styles.kioskChoiceAssetPlaceholder}>
                      Subir imagen para comer aqui
                    </span>
                  )}
                </div>
              </label>

              <label style={styles.brandAssetCard}>
                <span style={styles.label}>Box "Para llevar"</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) =>
                    handleKioskChoiceImageChange("kioskTakeawayImageUrl", event)
                  }
                  style={styles.input}
                  disabled={restaurantSettingsDisabled}
                />
                <div style={styles.kioskChoiceAssetPreviewWrap}>
                  {perfil.kioskTakeawayImageUrl ? (
                    <img
                      src={perfil.kioskTakeawayImageUrl}
                      alt={`Imagen de Para llevar para ${nombreMostrado}`}
                      style={styles.kioskChoiceAssetPreviewImage}
                    />
                  ) : (
                    <span style={styles.kioskChoiceAssetPlaceholder}>
                      Subir imagen para para llevar
                    </span>
                  )}
                </div>
              </label>
            </div>
          </article>
        ) : null}

      </section>

      <section style={styles.panel}>
        <div style={styles.panelHeader}>
          <div>
            <h2 style={styles.panelTitle}>Dias y horarios</h2>
            <span style={styles.helperText}>
              Puedes configurar cada dia por separado y sumar una segunda franja cuando el
              local corta al mediodia o vuelve a abrir por la noche.
            </span>
          </div>
          <span style={styles.scheduleSummary}>{horarioResumen}</span>
        </div>

        <div style={styles.scheduleGrid}>
          {(perfil.horarioDetallado || createDefaultHorarioDetallado()).map((dia) => (
            <article
              key={dia.id}
              style={{
                ...styles.scheduleCard,
                ...(dia.abierto ? {} : styles.scheduleCardClosed),
              }}
            >
              <div style={styles.scheduleHeader}>
                <strong style={styles.scheduleDay}>{dia.label}</strong>
                <label style={styles.toggleRow}>
                  <input
                    type="checkbox"
                    checked={dia.abierto}
                    onChange={(event) => toggleDiaAbierto(dia.id, event.target.checked)}
                    disabled={restaurantSettingsDisabled}
                  />
                  <span>{dia.abierto ? "Abierto" : "Cerrado"}</span>
                </label>
              </div>

              {dia.abierto ? (
                <div style={styles.shiftList}>
                  {dia.franjas.map((franja, index) => (
                    <div key={`${dia.id}-${index}`} style={styles.shiftRow}>
                      <div style={styles.timeGroup}>
                        <span style={styles.timeLabel}>Desde</span>
                        <input
                          type="time"
                          value={franja.apertura}
                          onChange={(event) =>
                            updateFranja(dia.id, index, "apertura", event.target.value)
                          }
                          style={styles.timeInput}
                          disabled={restaurantSettingsDisabled}
                        />
                      </div>
                      <div style={styles.timeGroup}>
                        <span style={styles.timeLabel}>Hasta</span>
                        <input
                          type="time"
                          value={franja.cierre}
                          onChange={(event) =>
                            updateFranja(dia.id, index, "cierre", event.target.value)
                          }
                          style={styles.timeInput}
                          disabled={restaurantSettingsDisabled}
                        />
                      </div>

                      {dia.franjas.length > 1 && (
                        <button
                          type="button"
                          style={styles.miniDangerButton}
                          onClick={() => removeFranja(dia.id, index)}
                          disabled={restaurantSettingsDisabled}
                        >
                          Quitar
                        </button>
                      )}
                    </div>
                  ))}

                  {dia.franjas.length < 2 && (
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => addFranja(dia.id)}
                      disabled={restaurantSettingsDisabled}
                    >
                      + Agregar franja
                    </button>
                  )}
                </div>
              ) : (
                <div style={styles.closedText}>No abre este dia.</div>
              )}
            </article>
          ))}
        </div>
      </section>

      <section style={styles.panel}>
        <div style={styles.panelHeader}>
          <h2 style={styles.panelTitle}>Caracteristicas del restaurante</h2>
          <span style={styles.helperText}>
            Esto ayuda a definir como se presenta el local al cliente.
          </span>
        </div>

        <div style={styles.chipWrap}>
          {chipsDisponibles.map((item) => {
            const active = (perfil.caracteristicas || []).includes(item);

            return (
              <button
                key={item}
                type="button"
                onClick={() => toggleCaracteristica(item)}
                style={{
                  ...styles.chipButton,
                  ...(active ? styles.chipButtonActive : {}),
                }}
                disabled={restaurantSettingsDisabled}
              >
                {item}
              </button>
            );
          })}
        </div>
      </section>

      <BranchOperationalModulesPanel
        branch={currentRestaurant}
        profile={perfil}
        disabled={restaurantSettingsDisabled}
        onSaved={(nextProfile) => {
          draftRevisionRef.current += 1;
          setPerfil(nextProfile);
          setEstadoGuardado("Modulos operativos actualizados para esta sucursal.");
        }}
      />

      <section style={styles.panel}>
        <div style={styles.panelHeader}>
          <div>
            <h2 style={styles.panelTitle}>Equipo de salon</h2>
            <span style={styles.helperText}>
              Desde aca sumas mozos para esta sucursal. Mas adelante enchufamos el correo
              automatico y la pantalla operativa final del mozo.
            </span>
          </div>
          <span style={styles.scheduleSummary}>
            {activeWaitersCount} activos · {pendingWaiterInvitations.length} pendientes
          </span>
        </div>

        {!canManageRestaurantUsers ? (
          <div style={{ ...styles.notice, ...styles.noticeError }}>
            {currentAccessRoleLabel} puede revisar el equipo de salon, pero no invitar,
            activar ni pausar mozos desde esta sucursal.
          </div>
        ) : null}

        {!waiterFeatureEnabled ? (
          <div style={{ ...styles.notice, ...styles.noticeError }}>
            El modulo de mozos esta pausado para esta sucursal. Puedes volver a activarlo
            arriba cuando quieras retomar el trabajo con salon.
          </div>
        ) : null}

        {estadoMozos ? (
          <div
            style={{
              ...styles.notice,
              ...(estadoMozos.includes("No se pudo") || estadoMozos.includes("Falta aplicar")
                ? styles.noticeError
                : styles.noticeOk),
            }}
          >
            {estadoMozos}
          </div>
        ) : null}

        <div style={styles.waiterGrid}>
          <form onSubmit={handleCreateWaiter} style={styles.waiterFormCard}>
            <div style={styles.waiterFormHeader}>
              <strong style={styles.waiterTitle}>Agregar mozo</strong>
              <span style={styles.helperText}>Alta rapida para la sucursal activa.</span>
            </div>

            <label style={styles.field}>
              <span style={styles.label}>Nombre y apellido</span>
              <input
                type="text"
                value={waiterForm.fullName}
                onChange={(event) =>
                  setWaiterForm((prev) => ({ ...prev, fullName: event.target.value }))
                }
                style={styles.input}
                placeholder="Ej: Juan Perez"
                disabled={loadingWaiters || restaurantUsersDisabled || !waiterFeatureEnabled}
              />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Correo</span>
              <input
                type="email"
                value={waiterForm.email}
                onChange={(event) =>
                  setWaiterForm((prev) => ({ ...prev, email: event.target.value }))
                }
                style={styles.input}
                placeholder="mozo@restaurante.com"
                disabled={loadingWaiters || restaurantUsersDisabled || !waiterFeatureEnabled}
              />
            </label>

            <button
              type="submit"
              style={styles.primaryButton}
              disabled={
                loadingWaiters || restaurantUsersDisabled || !waiterFeatureEnabled
              }
            >
              {loadingWaiters ? "Guardando..." : "Agregar mozo"}
            </button>
          </form>

          <div style={styles.waiterListCard}>
            <div style={styles.waiterFormHeader}>
              <strong style={styles.waiterTitle}>Mozos de esta sucursal</strong>
              <span style={styles.helperText}>
                Puedes pausarlos o volver a activarlos cuando arranca el turno.
              </span>
            </div>

            <div style={styles.waiterList}>
              {branchWaiters.length ? (
                branchWaiters.map((waiter) => (
                  <article key={waiter.userId} style={styles.waiterRow}>
                    <div style={styles.waiterIdentity}>
                      <strong style={styles.waiterName}>
                        {waiter.profileFullName || "Mozo pendiente de completar datos"}
                      </strong>
                      <span style={styles.waiterMeta}>
                        {waiter.profilePhone || "Sin telefono cargado"}
                      </span>
                    </div>

                    <div style={styles.waiterActions}>
                      <span
                        style={{
                          ...styles.waiterState,
                          ...(waiter.branchAccessActive
                            ? styles.waiterStateActive
                            : styles.waiterStatePaused),
                        }}
                      >
                        {waiter.branchAccessActive ? "Activo ahora" : "Pausado"}
                      </span>
                      <button
                        type="button"
                        style={styles.secondaryButton}
                        onClick={() => handleToggleWaiter(waiter)}
                        disabled={
                          loadingWaiters ||
                          restaurantUsersDisabled ||
                          !waiterFeatureEnabled
                        }
                      >
                        {waiter.branchAccessActive ? "Pausar" : "Activar"}
                      </button>
                    </div>
                  </article>
                ))
              ) : (
                <div style={styles.waiterEmpty}>
                  Todavia no hay mozos activos en esta sucursal.
                </div>
              )}
            </div>

            {pendingWaiterInvitations.length ? (
              <div style={styles.pendingWaitersBlock}>
                <strong style={styles.pendingWaitersTitle}>Invitaciones pendientes</strong>
                <div style={styles.pendingWaitersList}>
                  {pendingWaiterInvitations.map((invitation) => (
                    <div key={invitation.id} style={styles.pendingWaiterRow}>
                      <span style={styles.pendingWaiterName}>
                        {invitation.display_name || invitation.email}
                      </span>
                      <span style={styles.pendingWaiterEmail}>{invitation.email}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section
        style={{
          ...styles.previewPanel,
          backgroundColor: brandTheme.pageTint,
          color: "#0f172a",
          fontFamily: brandTheme.fontFamily,
        }}
      >
        <div
          style={{
            ...styles.previewBrandBar,
            background: brandTheme.heroBackground,
            color: brandTheme.secondaryColor,
          }}
        >
          <div style={styles.previewBrandIdentity}>
            {perfil.branding?.logoCompactUrl || perfil.branding?.logoPrimaryUrl ? (
              <img
                src={perfil.branding?.logoCompactUrl || perfil.branding?.logoPrimaryUrl}
                alt={`Marca de ${nombreMostrado}`}
                style={styles.previewBrandLogo}
              />
            ) : null}
            <div>
              <div style={styles.previewOverline}>Vista del comensal</div>
              <strong style={styles.previewTitle}>{nombreMostrado}</strong>
            </div>
          </div>
          <div
            style={{
              ...styles.previewBadge,
              backgroundColor: brandTheme.secondaryColor,
              color: brandTheme.primaryColor,
            }}
          >
            Identidad
          </div>
        </div>

        {perfil.imagenPortada ? (
          renderCoverFrame({
            src: perfil.imagenPortada,
            alt: `Portada visual de ${nombreMostrado}`,
            mode: perfil.coverDisplayMode,
            wrapStyle: styles.previewCoverWrap,
            imageStyle: styles.previewCover,
            centeredBackdropStyle: styles.previewCoverCenteredBackdrop,
            centeredOverlayStyle: styles.previewCoverCenteredOverlay,
            centeredImageStyle: styles.previewCoverCenteredImage,
          })
        ) : null}

        <p style={styles.previewDescription}>{perfil.descripcion}</p>

        <div style={styles.previewInfoGrid}>
          <div style={styles.previewInfoItem}>
            <span style={styles.previewLabel}>Direccion</span>
            <strong>{perfil.direccion || "Pendiente"}</strong>
          </div>
          <div style={styles.previewInfoItem}>
            <span style={styles.previewLabel}>Horario</span>
            <strong>{horarioResumen || "Pendiente"}</strong>
          </div>
          <div style={styles.previewInfoItem}>
            <span style={styles.previewLabel}>Telefono</span>
            <strong>{perfil.telefono || "Pendiente"}</strong>
          </div>
        </div>

        <div style={styles.previewScheduleBlock}>
          {(perfil.horarioDetallado || createDefaultHorarioDetallado()).map((dia) => (
            <div key={dia.id} style={styles.previewScheduleRow}>
              <span style={styles.previewScheduleDay}>{dia.label}</span>
              <span style={styles.previewScheduleValue}>{formatHorarioDia(dia)}</span>
            </div>
          ))}
        </div>

        <div style={styles.previewTagWrap}>
          {(perfil.caracteristicas || []).map((item) => (
            <span
              key={item}
              style={{
                ...styles.previewTag,
                backgroundColor: brandTheme.softColor,
                color: brandTheme.primaryColor,
              }}
            >
              {item}
            </span>
          ))}
        </div>

        <div
          style={{
            ...styles.previewMessage,
            border: `1px solid ${brandTheme.borderColor}`,
            backgroundColor: "#ffffff",
            color: "#334155",
          }}
        >
          {perfil.mensajeBienvenida}
        </div>
      </section>
    </div>
  );
}

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: "22px",
    padding: "8px 6px 20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "18px",
    flexWrap: "wrap",
  },
  overline: {
    color: "#2563eb",
    fontSize: "13px",
    fontWeight: 700,
    textTransform: "uppercase",
    marginBottom: "8px",
  },
  title: {
    marginTop: 0,
    marginBottom: "10px",
    color: "#0f172a",
    fontSize: "32px",
  },
  subtitle: {
    margin: 0,
    color: "#475569",
    lineHeight: 1.6,
    maxWidth: "820px",
  },
  primaryButton: {
    border: "none",
    borderRadius: "12px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    fontWeight: 700,
    padding: "12px 18px",
    cursor: "pointer",
  },
  notice: {
    borderRadius: "12px",
    padding: "12px 14px",
    fontWeight: 600,
  },
  noticeOk: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  noticeError: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fca5a5",
    color: "#b91c1c",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    gap: "18px",
    alignItems: "start",
  },
  gridSideColumn: {
    display: "grid",
    gap: "18px",
    alignContent: "start",
  },
  gridFullWidth: {
    gridColumn: "1 / -1",
  },
  panel: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #dbe2ea",
    padding: "20px",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.04)",
  },
  panelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "16px",
  },
  panelTitle: {
    marginTop: 0,
    marginBottom: "18px",
    color: "#0f172a",
    fontSize: "20px",
  },
  helperText: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 600,
    display: "block",
    maxWidth: "760px",
    lineHeight: 1.55,
  },
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "16px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    color: "#334155",
    fontWeight: 700,
    fontSize: "14px",
  },
  input: {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "12px 14px",
    fontSize: "15px",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    minHeight: "140px",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "12px 14px",
    fontSize: "15px",
    boxSizing: "border-box",
    resize: "vertical",
    fontFamily: "inherit",
    lineHeight: 1.5,
  },
  coverPreviewWrap: {
    marginBottom: "16px",
    position: "relative",
    width: "100%",
    height: "220px",
    overflow: "hidden",
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#edf2f7",
  },
  coverPreview: {
    width: "100%",
    height: "100%",
    objectPosition: "center",
    display: "block",
  },
  coverPreviewCenteredBackdrop: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
    filter: "blur(24px)",
    transform: "scale(1.08)",
    opacity: 0.4,
  },
  coverPreviewCenteredOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(180deg, rgba(237,242,247,0.16) 0%, rgba(237,242,247,0.32) 100%)",
  },
  coverPreviewCenteredImage: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center",
    display: "block",
  },
  coverPlaceholder: {
    marginBottom: "16px",
    padding: "14px 16px",
    borderRadius: "12px",
    border: "1px dashed #cbd5e1",
    color: "#64748b",
    backgroundColor: "#f8fafc",
  },
  coverModeRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "10px",
  },
  coverModeButton: {
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    padding: "14px",
    display: "grid",
    gap: "6px",
    textAlign: "left",
    cursor: "pointer",
    color: "#334155",
  },
  coverModeButtonActive: {
    backgroundColor: "#eff6ff",
    borderColor: "#93c5fd",
    boxShadow: "0 12px 24px rgba(37, 99, 235, 0.08)",
    color: "#1d4ed8",
  },
  coverModeHint: {
    fontSize: "12px",
    lineHeight: 1.45,
    color: "#64748b",
    fontWeight: 600,
  },
  scheduleSummary: {
    display: "inline-flex",
    alignItems: "center",
    padding: "10px 14px",
    borderRadius: "999px",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    fontWeight: 700,
    fontSize: "13px",
    maxWidth: "100%",
  },
  scheduleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "14px",
  },
  scheduleCard: {
    border: "1px solid #dbe2ea",
    borderRadius: "14px",
    padding: "16px",
    backgroundColor: "#fbfdff",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  scheduleCardClosed: {
    backgroundColor: "#f8fafc",
  },
  scheduleHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  scheduleDay: {
    color: "#0f172a",
    fontSize: "16px",
  },
  toggleRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    color: "#475569",
    fontWeight: 600,
  },
  shiftList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  shiftRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
    gap: "10px",
    alignItems: "end",
  },
  timeGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  timeLabel: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  timeInput: {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "11px 12px",
    fontSize: "14px",
    boxSizing: "border-box",
  },
  secondaryButton: {
    border: "1px solid #bfdbfe",
    borderRadius: "10px",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    padding: "10px 12px",
    fontWeight: 700,
    cursor: "pointer",
    alignSelf: "flex-start",
  },
  miniDangerButton: {
    border: "1px solid #fecaca",
    borderRadius: "10px",
    backgroundColor: "#fff1f2",
    color: "#be123c",
    padding: "10px 12px",
    fontWeight: 700,
    cursor: "pointer",
  },
  closedText: {
    color: "#64748b",
    fontWeight: 600,
  },
  chipWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
  },
  featureToggleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "14px",
  },
  featureToggleCard: {
    border: "1px solid #dbe2ea",
    borderRadius: "16px",
    padding: "18px",
    backgroundColor: "#fbfdff",
    display: "grid",
    gap: "12px",
  },
  featureToggleHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "14px",
    flexWrap: "wrap",
  },
  featureToggleCopy: {
    display: "grid",
    gap: "4px",
  },
  featureToggleTitle: {
    color: "#0f172a",
    fontSize: "16px",
  },
  featureToggleHint: {
    color: "#475569",
    fontSize: "13px",
    lineHeight: 1.55,
  },
  chipButton: {
    borderRadius: "999px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    color: "#334155",
    padding: "10px 14px",
    cursor: "pointer",
    fontWeight: 700,
  },
  chipButtonActive: {
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    borderColor: "#93c5fd",
  },
  waiterGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(260px, 360px) minmax(0, 1fr)",
    gap: "18px",
    alignItems: "start",
  },
  waiterFormCard: {
    border: "1px solid #dbe2ea",
    borderRadius: "16px",
    padding: "18px",
    backgroundColor: "#fbfdff",
    display: "grid",
    gap: "14px",
  },
  waiterListCard: {
    border: "1px solid #dbe2ea",
    borderRadius: "16px",
    padding: "18px",
    backgroundColor: "#ffffff",
    display: "grid",
    gap: "16px",
  },
  waiterFormHeader: {
    display: "grid",
    gap: "4px",
  },
  waiterTitle: {
    color: "#0f172a",
    fontSize: "16px",
  },
  waiterList: {
    display: "grid",
    gap: "10px",
  },
  waiterRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "14px",
    padding: "14px 16px",
    borderRadius: "14px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    flexWrap: "wrap",
  },
  waiterIdentity: {
    display: "grid",
    gap: "4px",
  },
  waiterName: {
    color: "#0f172a",
  },
  waiterMeta: {
    color: "#64748b",
    fontSize: "13px",
  },
  waiterActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  waiterState: {
    display: "inline-flex",
    alignItems: "center",
    padding: "7px 12px",
    borderRadius: "999px",
    fontWeight: 700,
    fontSize: "12px",
  },
  waiterStateActive: {
    backgroundColor: "#dcfce7",
    color: "#166534",
  },
  waiterStatePaused: {
    backgroundColor: "#fff7ed",
    color: "#c2410c",
  },
  waiterEmpty: {
    padding: "18px",
    borderRadius: "14px",
    backgroundColor: "#f8fafc",
    border: "1px dashed #cbd5e1",
    color: "#64748b",
  },
  pendingWaitersBlock: {
    display: "grid",
    gap: "10px",
    paddingTop: "4px",
  },
  pendingWaitersTitle: {
    color: "#334155",
  },
  pendingWaitersList: {
    display: "grid",
    gap: "8px",
  },
  pendingWaiterRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    padding: "12px 14px",
    borderRadius: "12px",
    backgroundColor: "#fff7ed",
    border: "1px solid #fed7aa",
    color: "#9a3412",
    flexWrap: "wrap",
  },
  pendingWaiterName: {
    fontWeight: 700,
  },
  pendingWaiterEmail: {
    fontSize: "13px",
  },
  brandAssetGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "14px",
    marginBottom: "18px",
  },
  brandAssetCard: {
    display: "grid",
    gap: "10px",
    padding: "16px",
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#fbfdff",
    cursor: "pointer",
  },
  brandAssetPreview: {
    minHeight: "96px",
    borderRadius: "14px",
    border: "1px dashed #cbd5e1",
    backgroundColor: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px",
    overflow: "hidden",
  },
  kioskChoiceAssetGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "14px",
  },
  kioskChoiceAssetPreviewWrap: {
    width: "100%",
    aspectRatio: "1.35 / 1",
    borderRadius: "16px",
    border: "1px dashed #cbd5e1",
    backgroundColor: "#ffffff",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  kioskChoiceAssetPreviewImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  kioskChoiceAssetPlaceholder: {
    padding: "0 18px",
    color: "#64748b",
    fontWeight: 700,
    fontSize: "13px",
    lineHeight: 1.4,
    textAlign: "center",
  },
  paymentConnectionCard: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: "14px",
    alignItems: "center",
    padding: "16px 18px",
    borderRadius: "16px",
    border: "1px solid #dbe2ea",
    background:
      "linear-gradient(135deg, rgba(248,250,252,0.96) 0%, rgba(239,246,255,0.82) 100%)",
    marginBottom: "18px",
  },
  paymentConnectionCopy: {
    display: "grid",
    gap: "8px",
  },
  paymentConnectionActions: {
    display: "grid",
    gap: "10px",
    justifyItems: "end",
    alignSelf: "center",
  },
  paymentConnectionStatus: {
    color: "#334155",
    fontSize: "13px",
    fontWeight: 700,
    lineHeight: 1.5,
  },
  oauthQrPanel: {
    display: "grid",
    gap: "16px",
    padding: "18px",
    borderRadius: "16px",
    border: "1px solid #bfdbfe",
    background:
      "linear-gradient(135deg, rgba(239,246,255,0.96) 0%, rgba(248,250,252,0.98) 100%)",
    marginBottom: "18px",
  },
  oauthQrCopy: {
    display: "grid",
    gap: "8px",
  },
  oauthQrBody: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, auto) minmax(220px, 1fr)",
    gap: "18px",
    alignItems: "center",
  },
  oauthQrCanvas: {
    width: "fit-content",
    padding: "16px",
    borderRadius: "16px",
    backgroundColor: "#ffffff",
    border: "1px solid #dbe2ea",
  },
  oauthQrActions: {
    display: "grid",
    gap: "10px",
    justifyItems: "start",
  },
  paymentConnectionHint: {
    borderRadius: "999px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    color: "#475569",
    fontSize: "13px",
    fontWeight: 700,
    padding: "10px 14px",
    alignSelf: "center",
    justifySelf: "start",
  },
  paymentMethodsLayout: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "18px",
    marginTop: "18px",
  },
  paymentMethodsGroup: {
    display: "grid",
    gap: "10px",
    padding: "16px",
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#fbfdff",
  },
  paymentGroupTitle: {
    color: "#0f172a",
    fontSize: "16px",
  },
  paymentOptionList: {
    display: "grid",
    gap: "10px",
  },
  paymentOptionRow: {
    display: "grid",
    gridTemplateColumns: "auto minmax(0, 1fr)",
    gap: "10px",
    alignItems: "start",
    padding: "12px 14px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
  },
  paymentOptionCopy: {
    display: "grid",
    gap: "4px",
    color: "#334155",
  },
  brandAssetPlaceholder: {
    color: "#64748b",
    fontWeight: 700,
    fontSize: "13px",
  },
  brandAssetHint: {
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.4,
  },
  logoPrimaryPreview: {
    maxWidth: "100%",
    maxHeight: "88px",
    objectFit: "contain",
    display: "block",
  },
  logoCompactPreview: {
    width: "auto",
    height: "auto",
    maxWidth: "100%",
    maxHeight: "72px",
    objectFit: "contain",
    display: "block",
  },
  logoIconPreview: {
    width: "56px",
    height: "56px",
    objectFit: "contain",
    display: "block",
  },
  brandControlsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "16px",
    marginBottom: "16px",
  },
  colorControlRow: {
    display: "grid",
    gridTemplateColumns: "70px minmax(0, 1fr)",
    gap: "10px",
    alignItems: "center",
  },
  colorInput: {
    width: "100%",
    height: "48px",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "4px",
    backgroundColor: "#ffffff",
    boxSizing: "border-box",
  },
  suggestedPaletteRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "18px",
  },
  suggestedPaletteLabel: {
    color: "#475569",
    fontSize: "13px",
    fontWeight: 700,
  },
  suggestedSwatches: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  suggestedSwatch: {
    width: "28px",
    height: "28px",
    borderRadius: "999px",
    border: "1px solid rgba(15, 23, 42, 0.08)",
  },
  fontOptionsWrap: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "12px",
  },
  fontOptionButton: {
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    padding: "14px",
    display: "grid",
    gap: "6px",
    textAlign: "left",
    cursor: "pointer",
  },
  fontOptionButtonActive: {
    boxShadow: "0 12px 24px rgba(15, 23, 42, 0.06)",
  },
  fontOptionSample: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.4,
  },
  loadingBrandRow: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 320px)",
    gap: "16px",
    marginTop: "18px",
  },
  loadingPreviewBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: "999px",
    border: "1px solid #bfdbfe",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    fontSize: "13px",
    fontWeight: 700,
  },
  loadingPreviewStageLarge: {
    minHeight: "240px",
    borderRadius: "18px",
    background:
      "radial-gradient(circle at top, rgba(255,255,255,0.98) 0%, rgba(248,250,252,1) 72%)",
    border: "1px dashed #cbd5e1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  loadingPreviewLogoLarge: {
    width: "112px",
    height: "112px",
    objectFit: "contain",
    display: "block",
  },
  loadingPreviewFallbackLarge: {
    maxWidth: "260px",
    color: "#64748b",
    fontWeight: 700,
    fontSize: "14px",
    lineHeight: 1.6,
    textAlign: "center",
  },
  loadingPreviewCaption: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 600,
    display: "block",
    lineHeight: 1.55,
    marginTop: "14px",
  },
  loadingLogoSpin: {
    animation: "mozzoapp-loading-spin 1.8s linear infinite",
  },
  loadingLogoPulse: {
    animation: "mozzoapp-loading-pulse 1.45s ease-in-out infinite",
  },
  loadingLogoFloat: {
    animation: "mozzoapp-loading-float 1.9s ease-in-out infinite",
  },
  previewPanel: {
    backgroundColor: "#0f172a",
    color: "#f8fafc",
    borderRadius: "18px",
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "18px",
    boxShadow: "0 18px 44px rgba(15, 23, 42, 0.18)",
  },
  previewBrandBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    padding: "18px 20px",
    borderRadius: "18px",
    flexWrap: "wrap",
  },
  previewBrandIdentity: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    minWidth: 0,
  },
  previewBrandLogo: {
    width: "64px",
    height: "64px",
    borderRadius: "16px",
    objectFit: "contain",
    backgroundColor: "rgba(255,255,255,0.18)",
    padding: "8px",
    display: "block",
  },
  previewCoverWrap: {
    marginBottom: "4px",
    position: "relative",
    width: "100%",
    minHeight: "240px",
    overflow: "hidden",
    borderRadius: "18px",
    backgroundColor: "#e2e8f0",
  },
  previewCover: {
    width: "100%",
    height: "240px",
    objectPosition: "center",
    display: "block",
  },
  previewCoverCenteredBackdrop: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
    filter: "blur(26px)",
    transform: "scale(1.08)",
    opacity: 0.42,
  },
  previewCoverCenteredOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.18) 100%)",
  },
  previewCoverCenteredImage: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    height: "240px",
    objectFit: "contain",
    objectPosition: "center",
    display: "block",
  },
  previewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
  },
  previewOverline: {
    color: "#93c5fd",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    marginBottom: "6px",
  },
  previewTitle: {
    fontSize: "28px",
  },
  previewBadge: {
    borderRadius: "999px",
    padding: "8px 12px",
    fontWeight: 700,
    fontSize: "13px",
  },
  previewDescription: {
    margin: 0,
    color: "#334155",
    lineHeight: 1.7,
    maxWidth: "880px",
  },
  previewInfoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "12px",
  },
  previewInfoItem: {
    padding: "14px",
    borderRadius: "14px",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  previewLabel: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  previewScheduleBlock: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "10px",
  },
  previewScheduleRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
    padding: "12px 14px",
    borderRadius: "14px",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
  },
  previewScheduleDay: {
    color: "#334155",
    fontWeight: 700,
  },
  previewScheduleValue: {
    color: "#475569",
    textAlign: "right",
  },
  previewTagWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
  },
  previewTag: {
    padding: "8px 12px",
    borderRadius: "999px",
    backgroundColor: "rgba(59,130,246,0.16)",
    color: "#bfdbfe",
    fontWeight: 700,
    fontSize: "13px",
  },
  previewMessage: {
    borderRadius: "14px",
    padding: "18px",
    backgroundColor: "#ffffff",
    color: "#334155",
    lineHeight: 1.7,
  },
};

export default MiRestaurante;
